/**
 * Coerces a BigQuery numeric cell to a finite number.
 * @param value - Raw cell from the BigQuery client.
 * @returns Parsed number or 0 when not numeric.
 */
export const coerceBhConnectionNumber = (value: unknown): number => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'object' && value !== null && 'value' in value) {
    return coerceBhConnectionNumber((value as { value: unknown }).value);
  }
  const parsed = Number.parseFloat(String(value));
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Coerces a BigQuery count column to a non-negative integer.
 * @param value - Raw count cell.
 * @returns Integer count.
 */
export const coerceBhConnectionCount = (value: unknown): number =>
  Math.max(0, Math.trunc(coerceBhConnectionNumber(value)));

/**
 * Coerces a warehouse percent field to a 0–100 integer for display.
 * @param value - Raw percent (0–1 ratio or 0–100).
 * @returns Rounded percentage.
 */
export const coerceBhConnectionPercent = (value: unknown): number => {
  const raw = coerceBhConnectionNumber(value);
  if (raw <= 0) {
    return 0;
  }
  const pct = raw <= 1 ? raw * 100 : raw;
  return Math.round(pct);
};

/**
 * Coerces an average-days field, rounding to one decimal when needed.
 * @param value - Raw average cell.
 * @returns Rounded average days.
 */
export const coerceBhConnectionAverageDays = (value: unknown): number => {
  const raw = coerceBhConnectionNumber(value);
  return Math.round(raw * 10) / 10;
};
